import { Link, useForm, router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft, CheckCircle2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'

type Props = {
  title: string
  breadcrumbs: { label: string; href?: string }[]
  backUrl: string
  receipt: {
    number: string
    amount_cents: number
  }
  createPath: string
}

export default function RefundNew({ title, breadcrumbs, backUrl, receipt, createPath }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    amount_cents: receipt.amount_cents,
    reason: ''
  })

  // We ask for input in dollars but submit in cents
  const displayAmount = (data.amount_cents / 100).toFixed(2)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (confirm("Are you sure you want to issue this refund? This action cannot be undone.")) {
      post(createPath)
    }
  }

  return (
    <DashboardLayout title="Issue Refund" breadcrumbs={breadcrumbs}>
      <div className="max-w-xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link href={backUrl}><ArrowLeft className="w-4 h-4" /></Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Issue Refund</h1>
        </div>

        <form onSubmit={handleSubmit} className="bg-card border rounded-lg p-6 space-y-6">
          <div className="p-4 bg-muted rounded-md space-y-1">
            <p className="text-sm font-medium">Original Receipt: {receipt.number}</p>
            <p className="text-sm text-muted-foreground">Max Refundable: {formatCurrency(receipt.amount_cents)}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Refund Amount (USD)</Label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-muted-foreground">$</span>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0.01"
                max={(receipt.amount_cents / 100).toString()}
                className="pl-7"
                value={displayAmount}
                onChange={(e) => {
                  const val = parseFloat(e.target.value)
                  if (!isNaN(val)) setData('amount_cents', Math.round(val * 100))
                }}
                required
              />
            </div>
            {errors.amount_cents && <p className="text-sm text-destructive">{errors.amount_cents}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Refund</Label>
            <Textarea
              id="reason"
              rows={4}
              value={data.reason}
              onChange={(e) => setData('reason', e.target.value)}
              placeholder="Provide a clear reason for issuing this refund..."
              required
            />
            {errors.reason && <p className="text-sm text-destructive">{errors.reason}</p>}
          </div>

          <Button type="submit" className="w-full" disabled={processing} variant="destructive">
            Process Refund
          </Button>
        </form>
      </div>
    </DashboardLayout>
  )
}
